import { Flex, FormGroup, FormLabel, Grid } from "@eworkit/iris";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { ITestable } from "@/browser";
import { FormikDatePicker, FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { IChangeable, IStyleFlexDirection } from "@/eworkitComponents";
import { IconGroup } from "@/eworkitMove/IconGroup";
import { withFormikField } from "@/formikComponents";
import { useFormats } from "@/hooks/Formats";
import { ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { FormikCodelistGenericSelect } from "@/views/components/Formik";

export const validationAvailabilitySectionSchema = Yup.object().shape({
  availability: Yup.string().nullable(true).required(VALIDATION_TYPES.REQUIRED),
  availabilityDate: Yup.string()
    .nullable(true)
    .when("availability", {
      is: v => v === "SPECIFIC_DATE",
      then: Yup.string().required(VALIDATION_TYPES.REQUIRED),
    }),
});

const AVAILABILITY_LEVELS_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "availability" };

export interface IConsultantProfileAvailabiltySectionProps
  extends IChangeable,
    ITestable,
    Partial<IStyleFlexDirection>,
    Pick<IConsultantProfile, "availability" | "availabilityDate"> {
  name: string;
  size?: string;
  setAvailabilityDateField: (date: string | null) => void;
}

const AvailabilitySectionFormComponent: React.FC<IConsultantProfileAvailabiltySectionProps> = React.memo(props => {
  const { availability, availabilityDate, onChange, testName, setAvailabilityDateField, size, direction, name } = props;

  const { t } = useTranslation("session");
  const { dateFormat, dateTimeFormat, formatDate } = useFormats();

  const change = (v: IConsultantProfileAvailabiltySectionProps["availability"]) => {
    if (onChange) {
      onChange(v, name);
      if (v === "SPECIFIC_DATE") {
        setAvailabilityDateField(moment().format("YYYY-MM-DD"));
      } else {
        setAvailabilityDateField(null);
      }
    }
  };

  return (
    <Flex flexGrow={1} gap="sm" flexDirection="column">
      <Flex flexDirection={direction}>
        <FormGroup
          label={<FormLabel label={t("CONSULTANT_PROFILES.DETAIL.AVAILABILITY.NOTICE_PERIOD_LABEL")} required />}
          error={<FormikError name="availability" data-test={`error-${testName}`} />}>
          <FormikCodelistGenericSelect
            name={name}
            onChange={change}
            query={AVAILABILITY_LEVELS_CODELIST_QUERY}
            required
            simpleValue
            value={availability}
            size={size}
            placeholder={t("CONSULTANT_PROFILES.DETAIL.AVAILABILITY.NOTICE_PERIOD_PLACEHOLDER")}
          />
        </FormGroup>
      </Flex>

      {availability === "SPECIFIC_DATE" && (
        <Grid columns={["1fr", null, null, "1fr 1fr"]}>
          <Flex flexDirection={direction}>
            <FormGroup error={<FormikError name="availabilityDate" data-test="error-availabilityDate" />}>
              <IconGroup icon="date" size="lg">
                <FormikDatePicker
                  minDate={moment() as any}
                  name="availabilityDate"
                  apiFormat={{
                    date: "YYYY-MM-DD",
                  }}
                  displayFormat={{
                    date: dateFormat,
                    dateTime: dateTimeFormat,
                  }}
                  onChange={(date: Date | null) => setAvailabilityDateField(date ? date.toISOString() : null)}
                  placeholder={t("CONSULTANT_PROFILES.DETAIL.AVAILABILITY.AVAILABILITY_DATE_PLACEHOLDER")}
                  value={availabilityDate ? new Date(availabilityDate) : null}
                  size={size}>
                  {formatDate(availabilityDate)}
                </FormikDatePicker>
              </IconGroup>
            </FormGroup>
          </Flex>
        </Grid>
      )}
    </Flex>
  );
});

export const AvailabilitySectionForm = withFormikField<IConsultantProfileAvailabiltySectionProps>(
  AvailabilitySectionFormComponent,
);
